import React, { useState, useEffect } from 'react';
import { Chess } from 'chess.js';
import ChessBoard from '../../components/ChessBoard';
import api from '../../api/client';
import { engineManager, EngineEvaluation } from '../game/engine/engineManager';
import { RefreshCw, CheckCircle2, XCircle, RotateCcw, Lightbulb, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Brain, Terminal, BarChart2 } from 'lucide-react';
import { getOpeningData, buildEvidencePackage, EvidencePackage, levelToDepth, levelToElo } from '@chess-platform/shared';
import CoachPanel from '../../components/CoachPanel';

const STARTING_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

interface OpeningTrainingProps {
    eco?: string;
    color?: 'w' | 'b';
    onExit?: () => void;
}

const OpeningTraining: React.FC<OpeningTrainingProps> = ({ eco: initialEco, color: initialColor, onExit }) => {
    const [selectedEco, setSelectedEco] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [showMobileAnalysis, setShowMobileAnalysis] = useState(false);

    const [currentFen, setCurrentFen] = useState(STARTING_FEN);
    const [targetMoves, setTargetMoves] = useState<string[]>([]);
    const [explanations, setExplanations] = useState<string[]>([]);
    const [moveIndex, setMoveIndex] = useState(0);
    const [status, setStatus] = useState<'idle' | 'waiting_user' | 'correct' | 'wrong' | 'finished'>('idle');
    const [playerColor, setPlayerColor] = useState<'w' | 'b'>('w');
    const [showHint, setShowHint] = useState(false);
    const [engineEval, setEngineEval] = useState<EngineEvaluation | null>(null);
    const [isFreePlay, setIsFreePlay] = useState(false);
    const [freePlayElo, setFreePlayElo] = useState(10);

    const [viewIndex, setViewIndex] = useState(0);
    const [currentFenHistory, setCurrentFenHistory] = useState<string[]>([]);

    // Coach/Evidence for free play
    const [evidence, setEvidence] = useState<EvidencePackage | null>(null);
    const [coachExplanation, setCoachExplanation] = useState<{
        short: string;
        details: string[];
        ifInstead?: string;
        warnings?: string[];
    } | null>(null);
    const [coachLoading, setCoachLoading] = useState(false);

    useEffect(() => {
        const unsub = engineManager.subscribe((ev: EngineEvaluation) => setEngineEval(ev));
        return () => unsub();
    }, []);

    useEffect(() => {
        if (status === 'waiting_user' && viewIndex === moveIndex) {
            engineManager.analyze(currentFen, 'depth 12');
        }
    }, [currentFen, status, viewIndex, moveIndex]);

    useEffect(() => {
        if (initialEco) startTraining(initialEco, initialColor || 'w');
    }, [initialEco, initialColor]);

    const startTraining = (eco: string, color: 'w' | 'b' = 'w') => {
        setSelectedEco(eco);
        setPlayerColor(color);
        setStatus('idle');
        setIsFreePlay(false);
        setLoading(true);
        setEvidence(null);
        setCoachExplanation(null);

        const bookData = getOpeningData(eco);
        if (bookData) {
            setThemeFromBook(bookData.moves || [], bookData.explanations || [], color);
        } else {
            api.get(`/openings/${eco}`).then(res => {
                const dataJson = res.data.dataJson ? JSON.parse(res.data.dataJson) : null;
                const line = dataJson?.mainLine || [];
                const expl = dataJson?.explanations || Array(line.length).fill('Movimiento teórico.');
                setThemeFromBook(line, expl, color);
            }).catch(() => setLoading(false));
        }
    };

    const triggerAiMove = async (userFen: string, currentHistory: string[]) => {
        setStatus('idle');
        const aiMove = await engineManager.getBestMoveOnce(userFen, 12);
        if (aiMove) {
            const aiGame = new Chess(userFen);
            try {
                aiGame.move({ from: aiMove.substring(0, 2), to: aiMove.substring(2, 4), promotion: aiMove.length === 5 ? aiMove.charAt(4) : undefined });
                const aiFen = aiGame.fen();
                setCurrentFen(aiFen);
                setCurrentFenHistory(prev => [...currentHistory, aiFen]);
                setMoveIndex(currentHistory.length + 1);
                setViewIndex(currentHistory.length + 1);
                setStatus('waiting_user');
            } catch (e) {
                console.error("AI returned invalid move", aiMove, e);
                setStatus('waiting_user');
            }
        } else {
            console.warn("AI returned no move");
            setStatus('waiting_user');
        }
    };

    const setThemeFromBook = (line: string[], expl: string[], color: 'w' | 'b') => {
        setTargetMoves(line);
        setExplanations(expl);
        setMoveIndex(0);
        const g = new Chess();
        setCurrentFen(g.fen());
        setCurrentFenHistory([g.fen()]);
        setShowHint(false);
        if (color === 'w') {
            setStatus('waiting_user');
        } else {
            const compMove = line[0];
            if (compMove) {
                setTimeout(() => {
                    const nextGame = new Chess();
                    try {
                        if (compMove.length >= 4 && compMove.charAt(0) >= 'a' && compMove.charAt(1) >= '1') {
                            nextGame.move({ from: compMove.substring(0, 2), to: compMove.substring(2, 4), promotion: compMove.length === 5 ? compMove.charAt(4) : undefined });
                        } else {
                            nextGame.move(compMove);
                        }
                        const nextFen = nextGame.fen();
                        setCurrentFen(nextFen);
                        setCurrentFenHistory([g.fen(), nextFen]);
                        setMoveIndex(1);
                        setStatus('waiting_user');
                    } catch (e) {
                        setStatus('waiting_user');
                    }
                }, 600);
            } else {
                setStatus('waiting_user');
            }
        }
        setLoading(false);
    };

    const handleMove = async (move: { from: string; to: string; promotion?: string }) => {
        if (status === 'idle') return;
        let activeFen = currentFen;
        let activeMoveIndex = moveIndex;
        let activeHistory = currentFenHistory;
        let currentlyFreePlay = isFreePlay;
        let didBranch = false;
        const preMoveEval = engineEval?.score || 0;
        const preMoveBestMove = engineEval?.bestMove || '';
        if (viewIndex < moveIndex) {
            activeHistory = currentFenHistory.slice(0, viewIndex + 1);
            activeFen = activeHistory[activeHistory.length - 1];
            activeMoveIndex = viewIndex;
            currentlyFreePlay = true;
            didBranch = true;
            setIsFreePlay(true);
        }
        const game = new Chess(activeFen);
        const moveRes = game.move(move);
        if (!moveRes) return;
        const playedUci = `${moveRes.from}${moveRes.to}${moveRes.promotion || ''}`;
        const userFen = game.fen();
        setCurrentFen(userFen);
        const updatedHistory = [...activeHistory, userFen];
        setCurrentFenHistory(updatedHistory);
        setMoveIndex(activeMoveIndex + 1);
        setViewIndex(activeMoveIndex + 1);

        if (currentlyFreePlay) {
            setStatus('idle');
            setCoachLoading(true);
            if (didBranch) setCoachExplanation({ short: 'Variante ramificada. Iniciando juego libre.', details: [] });
            else setCoachExplanation({ short: 'Respuesta del rival...', details: [] });
            engineManager.analyze(userFen, `depth ${levelToDepth(freePlayElo)}`);
            const aiMove = await engineManager.getBestMoveOnce(userFen, levelToDepth(freePlayElo));
            try {
                const evPkg = buildEvidencePackage({
                    ply: activeMoveIndex + 1,
                    fenBefore: activeFen,
                    fenAfter: userFen,
                    moveUci: playedUci,
                    moveSan: moveRes.san,
                    evalBeforePawns: preMoveEval,
                    evalAfterPawns: engineEval?.score || 0,
                    isMate: !!engineEval?.isMate,
                    mateMoves: engineEval?.mateMoves,
                    bestMoveUci: preMoveBestMove || '',
                    uciHistory: [],
                });
                setEvidence(evPkg);
                if (!didBranch) setCoachExplanation({ short: aiMove ? 'Lucha...' : 'Final de partida.', details: [] });
            } catch (e) { }
            setCoachLoading(false);
            if (aiMove) {
                const aiGame = new Chess(userFen);
                aiGame.move({ from: aiMove.substring(0, 2), to: aiMove.substring(2, 4), promotion: aiMove.length === 5 ? aiMove.charAt(4) : undefined });
                const aiFen = aiGame.fen();
                setTimeout(() => {
                    setCurrentFen(aiFen);
                    setCurrentFenHistory(prev => [...prev.slice(0, updatedHistory.length), aiFen]);
                    setMoveIndex(updatedHistory.length + 1);
                    setViewIndex(updatedHistory.length + 1);
                    setStatus('waiting_user');
                }, 600);
            } else { setStatus('waiting_user'); }
            return;
        }

        const expectedMove = targetMoves[activeMoveIndex];
        let isMatch = false;
        if (expectedMove) {
            try {
                const tempGame = new Chess(activeFen);
                const tempRes = tempGame.move(expectedMove);
                if (tempRes) {
                    const expectedUci = `${tempRes.from}${tempRes.to}${tempRes.promotion || ''}`;
                    isMatch = (playedUci === expectedUci);
                }
            } catch (e) { }
        }

        if (isMatch) {
            setStatus('correct');
            const nextIdx = activeMoveIndex + 1;
            setTimeout(async () => {
                if (nextIdx >= targetMoves.length) {
                    setIsFreePlay(true);
                    setStatus('finished');
                    api.post(`/training/progress/${selectedEco}`, { progress: 100 }).catch(() => { });

                    const checkGame = new Chess(userFen);
                    if (checkGame.turn() !== playerColor) {
                        triggerAiMove(userFen, updatedHistory);
                    } else {
                        setStatus('waiting_user');
                    }
                } else {
                    const compMove = targetMoves[nextIdx];
                    const nextGame = new Chess(userFen);
                    try {
                        if (compMove && compMove.length >= 4 && compMove.charAt(0) >= 'a' && compMove.charAt(1) >= '1') {
                            nextGame.move({ from: compMove.substring(0, 2), to: compMove.substring(2, 4), promotion: compMove.length === 5 ? compMove.charAt(4) : undefined });
                        } else if (compMove) {
                            nextGame.move(compMove);
                        }
                        const compFen = nextGame.fen();
                        setCurrentFen(compFen);
                        const finalHist = [...updatedHistory, compFen];
                        setCurrentFenHistory(finalHist);
                        setMoveIndex(nextIdx + 1);
                        setViewIndex(nextIdx + 1);
                        setStatus('waiting_user');
                    } catch (e) {
                        console.error("Error applying theoretical computer move", e);
                        setIsFreePlay(true);
                        const checkGame = new Chess(userFen);
                        if (checkGame.turn() !== playerColor) {
                            triggerAiMove(userFen, updatedHistory);
                        } else {
                            setStatus('waiting_user');
                        }
                    }
                }
            }, 800);
        } else {
            setStatus('wrong');
        }
    };

    const handleContinueFromDeviation = async () => {
        setIsFreePlay(true);
        setStatus('idle');
        const checkGame = new Chess(currentFen);
        if (checkGame.turn() !== playerColor) {
            triggerAiMove(currentFen, currentFenHistory.slice(0, moveIndex + 1));
        } else {
            setStatus('waiting_user');
            setCoachExplanation({ short: 'Ramificación de variante. ¡Adelante!', details: [] });
        }
    };

    const handleAskCoach = async () => {
        setCoachLoading(true);
        try {
            const res = await api.post('/ai/coach', { evidence });
            setCoachExplanation(res.data);
        } catch (err) {
            setCoachExplanation({
                short: 'Error al contactar con el Maestro.',
                details: ['Verifica tu conexión.'],
                warnings: ['Servicio temporalmente no disponible']
            });
        }
        setCoachLoading(false);
    };

    const handleBestMove = async () => {
        if (!isFreePlay && targetMoves[moveIndex]) {
            const expected = targetMoves[moveIndex];
            const game = new Chess(currentFen);
            let res;
            if (expected.length >= 4 && expected.charAt(0) >= 'a' && expected.charAt(1) >= '1') {
                res = game.move({ from: expected.substring(0, 2), to: expected.substring(2, 4), promotion: expected.length === 5 ? expected.charAt(4) : undefined });
            } else {
                res = game.move(expected);
            }
            if (res) handleMove({ from: res.from, to: res.to, promotion: res.promotion });
            return;
        }
        const best = await engineManager.getBestMoveOnce(currentFen, 18);
        if (best) handleMove({ from: best.substring(0, 2), to: best.substring(2, 4), promotion: best.length === 5 ? best.charAt(4) : undefined });
    };

    useEffect(() => { setViewIndex(moveIndex); }, [moveIndex]);

    const navigateTo = (idx: number) => {
        if (idx < 0 || idx > moveIndex) return;
        setViewIndex(idx);
    };

    const displayedFen = currentFenHistory[viewIndex] || currentFen;

    if (loading && !selectedEco) return <div className="h-full flex flex-col items-center justify-center p-8 bg-[#0a0c10]"><RefreshCw className="animate-spin text-indigo-400 mb-4" /><span className="text-[10px] font-black text-white/20 uppercase">Configurando Mesa de Práctica</span></div>;

    return (
        <div className="flex flex-col lg:flex-row h-full overflow-hidden p-2 lg:p-8 animate-in fade-in bg-[#0a0c10] text-white selection:bg-indigo-500/30">
            {/* BOARD AREA */}
            <div className="flex-[3] flex flex-col gap-2 lg:gap-4 min-h-0 relative">
                {/* Status Bar */}
                <div className="flex items-center justify-between px-2 py-1 lg:py-0">
                    <div className="flex items-center gap-2">
                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[8px] lg:text-[10px] font-black uppercase tracking-widest ${status === 'waiting_user' ? 'text-indigo-400' : status === 'idle' ? 'text-amber-400' : 'text-white/40'}`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${status === 'waiting_user' ? 'bg-indigo-400 animate-pulse' : status === 'idle' ? 'bg-amber-400 animate-pulse' : 'bg-white/10'}`} />
                            <span>
                                {isFreePlay ? (status === 'waiting_user' ? 'Juego Libre - Tu Turno' : 'Juego Libre - IA') : (status === 'waiting_user' ? 'Tu turno' : status === 'idle' ? 'Calculando' : 'Atento')}
                            </span>
                        </div>
                    </div>
                    {onExit && (
                        <button onClick={onExit} className="p-2 rounded-xl bg-white/5 border border-white/5 text-white/20 hover:text-white transition-all"><XCircle size={14} /></button>
                    )}
                </div>

                {/* Board container */}
                <div className="flex-1 flex flex-col relative min-h-0">
                    <div className="flex-1 flex items-center justify-center p-2 lg:p-4 overflow-hidden relative">
                        <div className="w-full h-full max-w-[85vh] aspect-square relative">
                            {/* Correct/Wrong Notification Overlay */}
                            {status === 'correct' && !isFreePlay && (
                                <div className="absolute inset-0 z-20 flex items-center justify-center bg-green-500/20 backdrop-blur-[2px] rounded-2xl pointer-events-none animate-in fade-in duration-300">
                                    <CheckCircle2 size={80} className="text-green-500 shadow-2xl" />
                                </div>
                            )}
                            {status === 'wrong' && (
                                <div className="absolute inset-0 z-20 flex items-center justify-center bg-red-500/20 backdrop-blur-[2px] rounded-2xl pointer-events-none animate-in fade-in duration-300">
                                    <XCircle size={80} className="text-red-500 shadow-2xl" />
                                </div>
                            )}

                            <ChessBoard
                                fen={displayedFen}
                                onMove={(status !== 'idle') ? handleMove : undefined}
                                orientation={playerColor === 'w' ? 'white' : 'black'}
                                draggable={status !== 'idle'}
                                customArrows={showHint && viewIndex === moveIndex && engineEval?.bestMove ? [
                                    [engineEval.bestMove.substring(0, 2), engineEval.bestMove.substring(2, 4), 'rgba(34,197,94,0.6)']
                                ] : []}
                            />
                        </div>
                    </div>
                </div>

                {/* Movement Controls */}
                <div className="flex items-center justify-center gap-3 lg:gap-4 py-2 shrink-0 px-4">
                    <button onClick={() => startTraining(selectedEco!, playerColor)} className="p-3 rounded-2xl bg-white/5 border border-white/5 text-white/20 hover:bg-white/10 hover:text-white transition-all"><RotateCcw size={18} /></button>
                    <div className="flex items-center bg-white/5 rounded-2xl border border-white/5 p-1 gap-1">
                        <button onClick={() => navigateTo(viewIndex - 1)} disabled={viewIndex === 0} className="p-3 rounded-xl hover:bg-white/5 disabled:opacity-10 text-white/60"><ChevronLeft size={20} /></button>
                        <div className="px-4 text-[10px] font-black tabular-nums text-white/30 tracking-[0.2em]">P{viewIndex} / {moveIndex}</div>
                        <button onClick={() => navigateTo(viewIndex + 1)} disabled={viewIndex >= moveIndex} className="p-3 rounded-xl hover:bg-white/5 disabled:opacity-10 text-white/60"><ChevronRight size={20} /></button>
                    </div>

                    {/* MOBILE FEED TOGGLE */}
                    <button
                        onClick={() => setShowMobileAnalysis(!showMobileAnalysis)}
                        className={`lg:hidden p-3 rounded-2xl border transition-all ${showMobileAnalysis ? 'bg-white text-black border-white' : 'bg-white/5 text-white/20 border-white/10'}`}
                    >
                        <BarChart2 size={18} />
                    </button>
                </div>
            </div>

            {/* RIGHT PANEL - Drawer Responsive */}
            <div className={`
                flex-1 flex flex-col gap-4 lg:gap-6 w-full lg:min-w-[340px] lg:max-w-[420px] 
                fixed lg:relative inset-x-0 bottom-0 z-50 lg:z-auto bg-[#0a0c10] lg:bg-transparent
                transition-transform duration-500 ease-[cubic-bezier(0.16, 1, 0.3, 1)] 
                ${showMobileAnalysis ? 'translate-y-0 h-[70vh] lg:h-full' : 'translate-y-full lg:translate-y-0 h-0 lg:h-full'} 
                lg:flex overflow-hidden shrink-0 border-t lg:border-t-0 border-white/10 shadow-[0_-20px_50px_rgba(0,0,0,0.8)] lg:shadow-none
            `}>
                {/* Drag Handle */}
                <div className="lg:hidden flex justify-center py-3 shrink-0" onClick={() => setShowMobileAnalysis(false)}>
                    <div className="w-12 h-1.5 bg-white/10 rounded-full" />
                </div>

                <div className="flex-1 overflow-y-auto p-4 lg:p-0 flex flex-col gap-4 custom-scrollbar">
                    {/* Status & Feedback Card */}
                    {status === 'finished' && !isFreePlay && (
                        <div className="bg-green-500/10 text-green-400 p-6 rounded-3xl border border-green-500/20 flex flex-col gap-4 shadow-2xl animate-in zoom-in duration-500">
                            <div className="flex items-center gap-3 font-black text-xs uppercase tracking-[0.2em]"><CheckCircle2 size={18} className="shrink-0" /> Variante Dominada</div>
                            <div className="bg-white/5 p-4 rounded-2xl border border-white/5">
                                <span className="text-[10px] font-black text-green-400/60 uppercase tracking-widest block mb-3">Continuar juego vs IA</span>
                                <div className="flex justify-between items-end mb-2">
                                    <span className="text-xl font-black text-white">Nivel {freePlayElo}</span>
                                    <span className="text-[9px] font-bold text-white/40">{levelToElo(freePlayElo)} ELO</span>
                                </div>
                                <input
                                    type="range" min="1" max="20"
                                    value={freePlayElo}
                                    onChange={(e) => setFreePlayElo(parseInt(e.target.value))}
                                    className="w-full h-1.5 bg-green-900/40 rounded-full appearance-none cursor-pointer accent-green-400"
                                />
                            </div>
                            <button onClick={() => setIsFreePlay(true)} className="w-full bg-green-600 text-white font-black py-4 rounded-xl text-[10px] uppercase tracking-widest hover:bg-green-500 transition-all shadow-xl shadow-green-600/10 active:scale-[0.98]">Empezar Juego Libre</button>
                        </div>
                    )}

                    {status === 'wrong' && (
                        <div className="bg-red-500/10 text-red-500 p-6 rounded-3xl border border-red-500/20 flex flex-col gap-4 shadow-2xl">
                            <div className="flex items-center gap-3 font-black text-xs uppercase tracking-[0.2em]"><XCircle size={18} className="shrink-0" /> Error en Línea</div>
                            <div className="text-[10px] font-black text-red-400 uppercase bg-red-500/5 px-3 py-1.5 rounded-lg border border-red-500/10">Esperado: {targetMoves[moveIndex - 1] || '---'}</div>
                            <div className="grid grid-cols-2 gap-2 mt-2">
                                <button onClick={() => startTraining(selectedEco!, playerColor)} className="bg-white/5 text-white font-black py-3 rounded-xl text-[9px] uppercase tracking-widest hover:bg-white/10 transition-all border border-white/5">Reintentar</button>
                                <button onClick={handleContinueFromDeviation} className="bg-indigo-500 text-white font-black py-3 rounded-xl text-[9px] uppercase tracking-widest active:scale-95 shadow-lg shadow-indigo-500/20">Continuar</button>
                            </div>
                        </div>
                    )}

                    {status === 'correct' && !isFreePlay && moveIndex > 0 && (
                        <div className="bg-indigo-500/10 text-indigo-400 p-6 rounded-3xl border border-indigo-500/20 shadow-2xl relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500" />
                            <div className="flex items-center gap-2 mb-3 text-white/20"><Terminal size={14} /><span className="text-[8px] font-black uppercase tracking-widest">Feedback Teórico</span></div>
                            <p className="text-sm lg:text-base font-black italic text-white/90 leading-relaxed leading-tight">"{explanations[Math.floor((moveIndex - 1) / 2)] || 'Buen movimiento teórico.'}"</p>
                        </div>
                    )}

                    {isFreePlay && evidence && (
                        <CoachPanel
                            evidence={evidence}
                            onAskCoach={handleAskCoach}
                            coachExplanation={coachExplanation}
                            coachLoading={coachLoading}
                        />
                    )}

                    {/* Progress Monitor */}
                    <div className="bg-[#1a1c24] rounded-3xl border border-white/5 p-5 shadow-2xl shrink-0">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-[9px] font-black text-white/30 uppercase tracking-[0.2em]">Progreso en variante</span>
                            <span className="text-[11px] font-black text-white">{Math.round((moveIndex / targetMoves.length) * 100)}%</span>
                        </div>
                        <div className="w-full bg-white/5 h-2 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-indigo-500 shadow-[0_0_10px_rgba(99,102,241,0.5)] transition-all duration-700 ease-out"
                                style={{ width: `${(moveIndex / targetMoves.length) * 100}%` }}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-5">
                            <button
                                onClick={() => setShowHint(!showHint)}
                                className={`py-4 rounded-2xl flex items-center justify-center gap-2 transition-all border text-[9px] font-black uppercase tracking-widest ${showHint ? 'bg-amber-500/10 border-amber-500/20 text-amber-500 shadow-lg' : 'bg-white/5 border-white/5 text-white/20'}`}
                            >
                                <Lightbulb size={16} /> Pista
                            </button>
                            <button onClick={handleBestMove} className="py-4 rounded-2xl flex items-center justify-center gap-2 bg-white/5 border border-white/5 text-white/20 hover:text-white transition-all text-[9px] font-black uppercase tracking-widest">
                                <Brain size={16} className="text-indigo-400" /> Motor
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OpeningTraining;
